// src/redux/features/authApi.ts

import { userCreateURL } from '../../components/Data/baseURL';
import { api } from '../services/api';
import { setLogout } from '../slices/authApiSlice';

export interface CompanyUser {
  id: number;

  email: string;

  first_name?: string | null;
  last_name?: string | null;

  phone?: string | null;
  skype?: string | null;
  image?: string | null;

  is_manager: boolean;
  is_team_leader: boolean;
  is_dispatcher: boolean;
  is_active: boolean;

  date_joined?: string | null;
  last_login?: string | null;
}

export interface UserGroup {
  id: number;
  name?: string;
  user_set?: CompanyUser[];
  [key: string]: unknown;
}

export interface Company {
  id?: number;
  uf?: string;
  companyname?: string | null;

  logo?: string | number | null;

  stamp?: string | number | null;

  [key: string]: unknown;
}

export interface UserLanguage {
  value?: string;
  label?: string;

  [key: string]: unknown;
}

export interface User {
  id: number;

  first_name?: string | null;
  last_name?: string | null;
  email: string;

  phone?: string | null;
  skype?: string | null;
  image?: string | null;

  lang?: string | UserLanguage | null;

  is_manager?: boolean;
  is_team_leader?: boolean;
  is_dispatcher?: boolean;
  is_active?: boolean;

  subscription_type?: string | null;
  type_account?: string | null;

  [key: string]: unknown;
}

export interface UpdateUserPayload {
  first_name?: string | null;
  last_name?: string | null;
  phone?: string | null;
  skype?: string | null;
  image?: string | null;
  lang?: string | UserLanguage | null;
}

interface UpdateCompanyUserArgs {
  id: number | string;
  body: UpdateUserPayload;
}

interface LoginArgs {
  email: string;
  password: string;
}

interface LoginResponse {
  access?: string;
  refresh?: string;
  detail?: string;
  user?: User;
}

interface SocialAuthArgs {
  provider: string;
  state: string;
  code: string;
}

interface SocialAuthResponse {
  success: boolean;
  user: User;
}

interface RegisterArgs {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  re_password: string;
}

interface ActivationArgs {
  uid: string;
  token: string;
}

interface ResetPasswordConfirmArgs {
  uid: string;
  token: string;
  new_password: string;
  re_new_password: string;
}

interface ApiMessage {
  detail?: string;
  message?: string;
}

interface CreateCompanyUserArgs {
  username: string;
  password: string;
}

interface CreatedCompanyUser {
  id: number;
  email?: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  phone?: string | null;
  is_manager?: boolean;
  [key: string]: unknown;
}

export const authApi = api.injectEndpoints({
  endpoints: (builder) => ({
    retrieveUser: builder.query<User, void>({
      query: () => ({
        url: '/auth/users/me/',
        method: 'GET',
      }),

      providesTags: [
        {
          type: 'Account',
          id: 'ME',
        },
      ],
    }),

    updateCurrentUser: builder.mutation<User, UpdateUserPayload>({
      query: (body) => ({
        url: '/auth/users/me/',
        method: 'PATCH',
        body,
      }),

      invalidatesTags: [
        {
          type: 'Account',
          id: 'ME',
        },
      ],
    }),

    /*
     * Preserves the custom endpoint used by your existing component.
     * This is different from the Djoser /auth/users/{id}/ endpoint.
     */
    deleteUser: builder.mutation<void, number | string>({
      query: (id) => ({
        url: `/app/users/${id}/`,
        method: 'DELETE',
      }),

      invalidatesTags: (_result, _error, id) => [
        {
          type: 'Account',
          id: 'ME',
        },
        {
          type: 'User',
          id,
        },
        {
          type: 'User',
          id: 'LIST',
        },
      ],
    }),

    updateCompanyUser: builder.mutation<User, UpdateCompanyUserArgs>({
      query: ({ id, body }) => ({
        url: `/app/users/${id}/`,
        method: 'PATCH',
        body,
      }),

      invalidatesTags: (_result, _error, { id }) => [
        {
          type: 'User',
          id,
        },
        {
          type: 'User',
          id: 'LIST',
        },
      ],
    }),

    getProfile: builder.query<User, void>({
      query: () => ({
        url: '/api/profiles/',
        method: 'GET',
      }),
    }),

    socialAuthenticate: builder.mutation<SocialAuthResponse, SocialAuthArgs>({
      query: ({ provider, state, code }) => ({
        url: `/api/o/${provider}/`,
        method: 'POST',
        params: {
          state,
          code,
        },
        headers: {
          Accept: 'application/json',
        },
      }),
    }),

    login: builder.mutation<LoginResponse, LoginArgs>({
      query: ({ email, password }) => ({
        url: '/api/jwt/create/',
        method: 'POST',
        body: {
          email,
          password,
        },
      }),
    }),

    register: builder.mutation<User, RegisterArgs>({
      query: ({ first_name, last_name, email, password, re_password }) => ({
        url: '/auth/users/',
        method: 'POST',
        body: {
          first_name,
          last_name,
          email,
          password,
          re_password,
        },
      }),
    }),

    /*
     * This is a query even though it uses POST.
     *
     * RTK Query can cache and deduplicate it, preventing duplicate
     * verification calls caused by React Strict Mode.
     */
    verify: builder.mutation<ApiMessage, void>({
      query: () => ({
        url: '/api/jwt/verify/',
        method: 'POST',
      }),
    }),

    refreshToken: builder.mutation<ApiMessage, void>({
      query: () => ({
        url: '/api/jwt/refresh/',
        method: 'POST',
      }),
    }),

    logout: builder.mutation<ApiMessage, void>({
      query: () => ({
        url: '/api/logout/',
        method: 'POST',
      }),

      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          /*
           * Local authentication state must still be cleared when
           * the logout request fails or the session already expired.
           */
        } finally {
          dispatch(setLogout());
          dispatch(api.util.resetApiState());
        }
      },
    }),

    activation: builder.mutation<ApiMessage, ActivationArgs>({
      query: ({ uid, token }) => ({
        url: '/auth/users/activation/',
        method: 'POST',
        body: {
          uid,
          token,
        },
      }),
    }),

    resetPassword: builder.mutation<ApiMessage, string>({
      query: (email) => ({
        url: '/auth/users/reset_password/',
        method: 'POST',
        body: {
          email,
        },
      }),
    }),

    resetPasswordConfirm: builder.mutation<ApiMessage, ResetPasswordConfirmArgs>({
      query: ({ uid, token, new_password, re_new_password }) => ({
        url: '/auth/users/reset_password_confirm/',
        method: 'POST',
        body: {
          uid,
          token,
          new_password,
          re_new_password,
        },
      }),
    }),

    getCompanyUsers: builder.query<CompanyUser[], void>({
      query: () => ({
        url: '/app/company-users/',
        method: 'GET',
      }),

      providesTags: (result) => [
        {
          type: 'User',
          id: 'LIST',
        },

        ...(result ?? []).map((user) => ({
          type: 'User' as const,
          id: user.id,
        })),
      ],
    }),

    createCompanyUser: builder.mutation<CreatedCompanyUser, CreateCompanyUserArgs>({
      query: ({ username, password }) => ({
        url: userCreateURL,
        method: 'POST',
        body: {
          username,
          password,
        },
      }),

      invalidatesTags: [
        {
          type: 'Account',
          id: 'ME',
        },
        {
          type: 'User',
          id: 'LIST',
        },
      ],
    }),
  }),
});

export const {
  useRetrieveUserQuery,
  useLazyRetrieveUserQuery,
  useUpdateCurrentUserMutation,
  useDeleteUserMutation,

  useUpdateCompanyUserMutation,

  useGetProfileQuery,
  useSocialAuthenticateMutation,
  useLoginMutation,
  useRegisterMutation,
  useVerifyMutation,
  useRefreshTokenMutation,
  useLogoutMutation,
  useActivationMutation,
  useResetPasswordMutation,
  useResetPasswordConfirmMutation,

  useGetCompanyUsersQuery,
  useCreateCompanyUserMutation,
} = authApi;
